import { createMachine, assign } from 'xstate';
import { useMachine } from '@xstate/react';
import { useCallback, useEffect } from 'react';
import Cookies from 'js-cookie';
import { World, defaultWorld, MusicMode } from '@/data/worlds';

export interface Task {
  id: string;
  text: string;
  completed: boolean;
}

export interface SessionContext {
  world: World;
  task: string; // Legacy: for initial task input in lobby
  tasks: Task[];
  currentTaskIndex: number;
  duration: number; // in minutes
  remaining: number; // in seconds
  musicMode: MusicMode;
  customYoutubeId: string | null;
  completedAt: Date | null;
}

export type SessionEvent =
  | { type: 'SELECT_WORLD'; world: World }
  | { type: 'SET_TASK'; task: string }
  | { type: 'SET_DURATION'; duration: number }
  | { type: 'SET_MUSIC_MODE'; mode: MusicMode; youtubeId?: string }
  | { type: 'ENTER' }
  | { type: 'TRANSITION_COMPLETE' }
  | { type: 'PAUSE' }
  | { type: 'RESUME' }
  | { type: 'TICK' }
  | { type: 'COMPLETE' }
  | { type: 'EXIT' }
  | { type: 'RESTART' }
  | { type: 'ADD_TASK'; text: string }
  | { type: 'COMPLETE_TASK' }
  | { type: 'SELECT_TASK'; index: number }
  | { type: 'REMOVE_TASK'; id: string };

const loadSavedSettings = (): Partial<SessionContext> => {
  const savedDuration = Cookies.get('duration');
  const savedMode = Cookies.get('mode') as MusicMode | undefined;
  const savedYoutubeId = Cookies.get('youtube');
  const savedWorld = Cookies.get('lastWorld');

  return {
    duration: savedDuration ? parseInt(savedDuration, 10) : 25,
    musicMode: savedMode || 'lofi',
    customYoutubeId: savedYoutubeId || null,
  };
};

const savedSettings = loadSavedSettings();

// The page's own <title> (pre-rendered landing pages each have a unique one),
// restored whenever the user is back in the lobby
const lobbyTitle = typeof document !== 'undefined' ? document.title : '';

const generateTaskId = () => Math.random().toString(36).substring(2, 9);

export const sessionMachine = createMachine({
  types: {} as {
    context: SessionContext;
    events: SessionEvent;
  },
  id: 'session',
  initial: 'lobby',
  context: {
    world: defaultWorld,
    task: '',
    tasks: [],
    currentTaskIndex: 0,
    duration: savedSettings.duration || 25,
    remaining: (savedSettings.duration || 25) * 60,
    musicMode: savedSettings.musicMode || 'lofi',
    customYoutubeId: savedSettings.customYoutubeId || null,
    completedAt: null,
  },
  states: {
    lobby: {
      on: {
        SELECT_WORLD: {
          actions: assign(({ event }) => {
            console.log('XState SELECT_WORLD action, event:', event);
            return {
              world: (event as { type: 'SELECT_WORLD'; world: World }).world,
            };
          }),
        },
        SET_TASK: {
          actions: assign({
            task: ({ event }) => event.task,
          }),
        },
        SET_DURATION: {
          actions: assign({
            duration: ({ event }) => event.duration,
            remaining: ({ event }) => event.duration * 60,
          }),
        },
        SET_MUSIC_MODE: {
          actions: assign({
            musicMode: ({ event }) => event.mode,
            customYoutubeId: ({ event }) => event.youtubeId || null,
          }),
        },
        ENTER: {
          target: 'transitioning',
          // No guard - can enter without tasks
          actions: assign({
            tasks: ({ context }) => {
              // Convert initial task input to tasks array if provided
              if (context.task.trim().length > 0) {
                return [{
                  id: generateTaskId(),
                  text: context.task.trim(),
                  completed: false,
                }];
              }
              return [];
            },
            currentTaskIndex: () => 0,
          }),
        },
      },
    },
    transitioning: {
      after: {
        1800: { target: 'world' },
      },
      on: {
        TRANSITION_COMPLETE: { target: 'world' },
      },
    },
    world: {
      initial: 'running',
      states: {
        running: {
          on: {
            PAUSE: { target: 'paused' },
            TICK: {
              actions: assign({
                remaining: ({ context }) => Math.max(0, context.remaining - 1),
              }),
            },
            COMPLETE: { target: '#session.complete' },
          },
        },
        paused: {
          on: {
            RESUME: { target: 'running' },
          },
        },
      },
      on: {
        EXIT: { target: 'exiting' },
        ADD_TASK: {
          actions: assign({
            tasks: ({ context, event }) => [
              ...context.tasks,
              {
                id: generateTaskId(),
                text: event.text,
                completed: false,
              },
            ],
            // If no tasks or all tasks completed, point to the new task
            currentTaskIndex: ({ context }) => {
              const allCompleted = context.tasks.every((t) => t.completed);
              if (context.tasks.length === 0 || allCompleted) {
                return context.tasks.length; // Index of new task
              }
              return context.currentTaskIndex;
            },
          }),
        },
        COMPLETE_TASK: {
          actions: assign({
            tasks: ({ context }) =>
              context.tasks.map((t, i) =>
                i === context.currentTaskIndex ? { ...t, completed: true } : t
              ),
            // Auto-advance to next incomplete task
            currentTaskIndex: ({ context }) => {
              const nextIncomplete = context.tasks.findIndex(
                (t, i) => i > context.currentTaskIndex && !t.completed
              );
              if (nextIncomplete !== -1) return nextIncomplete;
              // If no more incomplete tasks after current, stay on current
              return context.currentTaskIndex;
            },
          }),
        },
        SELECT_TASK: {
          actions: assign({
            currentTaskIndex: ({ event }) => event.index,
          }),
        },
        REMOVE_TASK: {
          actions: assign({
            tasks: ({ context, event }) =>
              context.tasks.filter((t) => t.id !== event.id),
            currentTaskIndex: ({ context, event }) => {
              const removedIndex = context.tasks.findIndex((t) => t.id === event.id);
              if (removedIndex < context.currentTaskIndex) {
                return Math.max(0, context.currentTaskIndex - 1);
              }
              return Math.min(
                context.currentTaskIndex,
                context.tasks.length - 2
              );
            },
          }),
        },
      },
    },
    exiting: {
      after: {
        800: { target: 'lobby' },
      },
    },
    complete: {
      entry: assign({
        completedAt: () => new Date(),
      }),
      on: {
        RESTART: {
          target: 'lobby',
          actions: assign({
            remaining: ({ context }) => context.duration * 60,
            completedAt: () => null,
            task: () => '',
            tasks: () => [],
            currentTaskIndex: () => 0,
          }),
        },
        EXIT: { target: 'lobby' },
      },
    },
  },
});

export const useSession = () => {
  const [state, send] = useMachine(sessionMachine);

  // Timer tick effect
  useEffect(() => {
    if (state.matches('world.running')) {
      const interval = setInterval(() => {
        if (state.context.remaining <= 1) {
          send({ type: 'COMPLETE' });
        } else {
          send({ type: 'TICK' });
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [state, send]);

  // Persist settings to cookies
  useEffect(() => {
    Cookies.set('duration', String(state.context.duration));
    Cookies.set('mode', state.context.musicMode);
    if (state.context.customYoutubeId) {
      Cookies.set('youtube', state.context.customYoutubeId);
    }
    Cookies.set('lastWorld', state.context.world.id);
  }, [state.context.duration, state.context.musicMode, state.context.customYoutubeId, state.context.world.id]);

  // Update document title
  useEffect(() => {
    if (state.matches('world.running') || state.matches('world.paused')) {
      const minutes = Math.floor(state.context.remaining / 60);
      const seconds = state.context.remaining % 60;
      const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      const pausedMark = state.matches('world.paused') ? '❚❚ ' : '';
      document.title = `${pausedMark}${timeStr} · ${state.context.world.name} · Kaizen Flow`;
    } else if (state.matches('complete')) {
      document.title = 'Session complete · Kaizen Flow';
    } else {
      document.title = lobbyTitle || 'Kaizen Flow - Enter Your World';
    }
  }, [state, state.context.remaining, state.context.world.name]);

  const selectWorld = useCallback((world: World) => {
    send({ type: 'SELECT_WORLD', world });
  }, [send]);

  const setTask = useCallback((task: string) => {
    send({ type: 'SET_TASK', task });
  }, [send]);

  const setDuration = useCallback((duration: number) => {
    send({ type: 'SET_DURATION', duration });
  }, [send]);

  const setMusicMode = useCallback((mode: MusicMode, youtubeId?: string) => {
    send({ type: 'SET_MUSIC_MODE', mode, youtubeId });
  }, [send]);

  const enter = useCallback(() => {
    send({ type: 'ENTER' });
  }, [send]);

  const pause = useCallback(() => {
    send({ type: 'PAUSE' });
  }, [send]);

  const resume = useCallback(() => {
    send({ type: 'RESUME' });
  }, [send]);

  const togglePause = useCallback(() => {
    if (state.matches('world.running')) {
      send({ type: 'PAUSE' });
    } else if (state.matches('world.paused')) {
      send({ type: 'RESUME' });
    }
  }, [state, send]);

  const exit = useCallback(() => {
    send({ type: 'EXIT' });
  }, [send]);

  const restart = useCallback(() => {
    send({ type: 'RESTART' });
  }, [send]);

  const addTask = useCallback((text: string) => {
    send({ type: 'ADD_TASK', text });
  }, [send]);

  const completeTask = useCallback(() => {
    send({ type: 'COMPLETE_TASK' });
  }, [send]);

  const selectTask = useCallback((index: number) => {
    send({ type: 'SELECT_TASK', index });
  }, [send]);

  const removeTask = useCallback((id: string) => {
    send({ type: 'REMOVE_TASK', id });
  }, [send]);

  // Computed helpers
  const currentTask = state.context.tasks[state.context.currentTaskIndex] || null;
  const hasActiveTasks = state.context.tasks.some((t) => !t.completed);
  const completedTaskCount = state.context.tasks.filter((t) => t.completed).length;
  const totalTaskCount = state.context.tasks.length;

  return {
    state,
    context: state.context,
    // State checks
    isLobby: state.matches('lobby'),
    isTransitioning: state.matches('transitioning'),
    isInWorld: state.matches('world'),
    isRunning: state.matches('world.running'),
    isPaused: state.matches('world.paused'),
    isComplete: state.matches('complete'),
    isExiting: state.matches('exiting'),
    // Actions
    selectWorld,
    setTask,
    setDuration,
    setMusicMode,
    enter,
    pause,
    resume,
    togglePause,
    exit,
    restart,
    // Task management
    addTask,
    completeTask,
    selectTask,
    removeTask,
    // Task helpers
    currentTask,
    hasActiveTasks,
    completedTaskCount,
    totalTaskCount,
  };
};
